import {
  Avatar,
  TableRow,
  MenuItem,
  TableCell,
  Typography,
  IconButton,
  ListItemText,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { TopUpWithdrawTxType } from 'src/type/topup-withdraw';
import { TransactionType, TransactionStatus } from 'src/type/transaction';
import { fNumber } from 'src/utils/format-number';
import { shortString } from 'src/utils/format-string';

import { fDate, fTime } from 'src/utils/format-time';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useBoolean } from 'src/hooks/use-boolean';
import { txStatusLabel } from '../__gold-withdraw/utils';
import TopupApproveDialog from './topup-approve-dialog';
import WithdrawApproveDialog from './withdraw-approve-dialog';
import TopupWithdrawRejectDialog from './topup-withdraw-reject-dialog';
import TopupWithdrawViewDialog from './topup-withdraw-view-dialog';

type Props = {
  row: TopUpWithdrawTxType;
  handleReload: () => void;
};

export default function TopupWithdrawTableRow({ row, handleReload }: Props) {
  const popover = usePopover();
  const approveTopup = useBoolean();
  const approveWithdraw = useBoolean();
  const reject = useBoolean();
  const view = useBoolean();

  const { user, createdAt, type, status, creditAmount, updatedAt } = row;

  const handleClick = () => {
    popover.onClose();

    if (row.type === TransactionType.TOPUP_CREDIT) {
      approveTopup.onTrue();
    } else {
      approveWithdraw.onTrue();
    }
  };

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox" align="center">
          {type === TransactionType.TOPUP_CREDIT && (
            <img src="/assets/icons/common/ic-add-square.svg" alt="" />
          )}
          {type === TransactionType.WITHDRAW_CREDIT && (
            <img src="/assets/icons/common/ic-minus-square.svg" alt="" />
          )}
        </TableCell>

        <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar alt="" sx={{ mr: 2 }}>
            {user?.firstName?.[0].toUpperCase()}
          </Avatar>

          <ListItemText
            disableTypography
            primary={
              <Typography variant="body2" noWrap>
                {user?.firstName} {user?.lastName}
              </Typography>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" noWrap>
                #{shortString(user?.uid)}
              </Typography>
            }
          />
        </TableCell>

        <TableCell>
          <ListItemText
            primary={fDate(createdAt, 'DD MMMM BBBB')}
            secondary={fTime(createdAt)}
            primaryTypographyProps={{ typography: 'body2', noWrap: true }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell>
          {status === TransactionStatus.SUCCESS ? (
            <ListItemText
              primary={fDate(updatedAt, 'DD MMMM BBBB')}
              secondary={fTime(updatedAt)}
              primaryTypographyProps={{ typography: 'body2', noWrap: true }}
              secondaryTypographyProps={{
                mt: 0.5,
                component: 'span',
                typography: 'caption',
              }}
            />
          ) : (
            '-'
          )}
        </TableCell>

        <TableCell>{fNumber(creditAmount)}</TableCell>

        <TableCell>
          <Label
            variant="soft"
            color={
              (status === TransactionStatus.SUCCESS && 'success') ||
              (status === TransactionStatus.PENDING && 'warning') ||
              (status === TransactionStatus.REJECTED && 'error') ||
              (status === TransactionStatus.CANCEL && 'info') ||
              'default'
            }
          >
            {txStatusLabel(status)}
          </Label>
        </TableCell>

        {/* <TableCell align="right" sx={{ px: 2 }}>
        <Link href={paths.overview.topupWithdraw.detail(row.id)}>
          <IconButton>
            <Iconify icon="solar:arrow-right-outline" />
          </IconButton>
        </Link>
      </TableCell> */}

        <TableCell align="right" sx={{ px: 2 }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="top-right"
        sx={{ width: 160 }}
      >
        {status === TransactionStatus.PENDING && (
          <>
            <MenuItem onClick={handleClick} sx={{ color: 'success.main' }}>
              <Iconify icon="solar:check-square-bold" />
              อนุมัติ
            </MenuItem>
            <MenuItem
              onClick={() => {
                popover.onClose();
                reject.onTrue();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon="solar:close-square-bold" />
              ปฏิเสธ
            </MenuItem>
          </>
        )}

        <MenuItem
          onClick={() => {
            popover.onClose();
            view.onTrue();
          }}
        >
          <Iconify icon="solar:info-square-bold" />
          รายละเอียด
        </MenuItem>
      </CustomPopover>

      {approveTopup.value && (
        <TopupApproveDialog
          open={approveTopup.value}
          onClose={approveTopup.onFalse}
          handleReload={handleReload}
          topUpWithdrawTx={row}
        />
      )}

      {approveWithdraw.value && (
        <WithdrawApproveDialog
          open={approveWithdraw.value}
          onClose={approveWithdraw.onFalse}
          handleReload={handleReload}
          topUpWithdrawTx={row}
        />
      )}

      {reject.value && (
        <TopupWithdrawRejectDialog
          open={reject.value}
          onClose={reject.onFalse}
          handleReload={handleReload}
          topUpWithdrawTx={row}
        />
      )}

      {view.value && <TopupWithdrawViewDialog open={view.value} onClose={view.onFalse} tx={row} />}
    </>
  );
}
